import { useNavigate } from "react-router-dom";
import { Link } from "react-router-dom";
import { useToast } from "@chotca/ui";
import type { Gender } from "@chotca/config";
import {
  EditPostingForm as SharedEditPostingForm,
  PostingNotEditable,
  postingToEditValues,
  type PostingFormOutput,
} from "@chotca/posting-form";
import {
  usePosting,
  usePostingEditConstraints,
  useUpdatePosting,
  useArchiveOrgAddress,
  useCreateOrgAddress,
  useOrgAddresses,
  useUpdateOrgAddress,
  useUploadPostingImage,
} from "@chotca/api-client";
import { AddressPicker } from "@/components/address-picker";

export function EditPostingForm({ id }: { id: string }) {
  const nav = useNavigate();
  const { toast } = useToast();
  const postingQ = usePosting(id);
  const constraintsQ = usePostingEditConstraints(id);
  const posting = postingQ.data;
  const orgId = posting?.orgId ?? "";

  const addressesQ = useOrgAddresses(orgId || null);
  const createAddress = useCreateOrgAddress(orgId);
  const updateAddress = useUpdateOrgAddress(orgId);
  const archiveAddress = useArchiveOrgAddress(orgId);
  const uploadPreview = useUploadPostingImage(orgId);
  const update = useUpdatePosting(id);

  if (postingQ.isError) return null;

  if (postingQ.isPending || constraintsQ.isPending) {
    return (
      <section className="space-y-6">
        <div className="h-4 w-24 animate-pulse rounded bg-ink/10" />
        <div className="h-10 w-2/3 animate-pulse rounded bg-ink/10" />
        <div className="h-64 animate-pulse rounded-xl bg-ink/5" />
      </section>
    );
  }

  if (!posting) return null;

  const backHref = `/postings/${id}`;

  if (!constraintsQ.data?.editable) {
    return (
      <section className="space-y-6">
        <nav className="text-sm">
          <Link
            to={backHref}
            className="text-ink/60 hover:text-ink hover:underline"
          >
            ← {posting.title}
          </Link>
        </nav>
        <PostingNotEditable
          state={constraintsQ.data?.state ?? posting.state}
          onBack={() => nav(backHref)}
        />
      </section>
    );
  }

  async function handleSubmit(values: PostingFormOutput) {
    // previewImageKey: a fresh key replaces; cleared image (no url) sends null;
    // an untouched existing image (url present, no new key) is left as-is.
    const previewImageKey =
      values.previewImageKey != null
        ? values.previewImageKey
        : values.previewImageUrl == null
          ? null
          : undefined;
    await update.mutateAsync({
      title: values.title,
      description: values.description,
      venueType: values.venueType,
      salaryMin: values.salaryMin,
      salaryMax: values.salaryMax,
      salaryType: values.salaryType,
      headcount: values.headcount,
      benefits: values.benefits,
      skillsRequired: values.skillsRequired,
      skillsPreferred: values.skillsPreferred,
      requirements: {
        gender: (values.reqGender ?? null) as Gender | null,
        minAge: values.reqMinAge ?? null,
        maxAge: values.reqMaxAge ?? null,
        minHeightCm: values.reqMinHeightCm ?? null,
        motorbikeRequired: values.reqMotorbike,
        excludeVisibleTattoo: values.reqExcludeTattoo,
        kycCccdRequired: values.reqKyc,
      },
      softNotes: values.softNotes || null,
      ...(previewImageKey !== undefined ? { previewImageKey } : {}),
      addressIds: values.addressIds,
    });
    toast({ tone: "success", title: "Đã lưu thay đổi" });
    nav(backHref);
  }

  return (
    <section className="space-y-4">
      <nav className="text-sm">
        <Link
          to={backHref}
          className="text-ink/60 hover:text-ink hover:underline"
        >
          ← {posting.title}
        </Link>
      </nav>
      <SharedEditPostingForm
        loading={update.isPending}
        onSubmit={handleSubmit}
        initialValues={postingToEditValues(posting)}
        minHeadcount={constraintsQ.data?.committedHires}
        lockedAddressIds={constraintsQ.data?.lockedAddressIds}
        addressInterviewCounts={constraintsQ.data?.addressInterviewCounts}
        onCancel={() => nav(backHref)}
        onMobileBack={() => nav(backHref)}
        onUploadPreviewImage={async (file, onProgress) => {
          const r = await uploadPreview.mutateAsync({ file, onProgress });
          return { key: r.key, url: r.publicUrl };
        }}
        AddressPicker={AddressPicker}
        addressBundle={{
          addresses: addressesQ.data?.items ?? [],
          loading: addressesQ.isLoading,
          onCreate: (input) => createAddress.mutateAsync(input),
          onUpdate: (addrId, input) =>
            updateAddress.mutateAsync({ id: addrId, body: input }),
          onArchive: async (addrId) => {
            await archiveAddress.mutateAsync(addrId);
          },
        }}
      />
    </section>
  );
}
